import React, { useState, useEffect, useRef } from 'react';
import { REPORTING_ENDPOINTS } from '../../constants/apiEndpoints';

function ReportQuestionPopup({ isOpen, onClose, questionId, eval_id, type = 'questionnaire', question = null }) {
  const [reportText, setReportText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const timerRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Set up timer when success message is shown
    if (isSuccess) {
      // Set up the auto-close timer
      timerRef.current = setTimeout(() => {
        handleClose();
      }, 10000); // 10 seconds
      
      // Set up the countdown interval
      setCountdown(10);
      intervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    // Clean up timer and interval when component unmounts or when success state changes
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isSuccess]);

  const handleClose = () => {
    // Reset states and clear timer and interval
    setIsSuccess(false);
    setReportText('');
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Send POST request to the webhook
      const response = await fetch(REPORTING_ENDPOINTS.REPORT_ITEM, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          questionId: String(questionId),
          eval_id,
          reporttext: reportText,
          type,
          question: question
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit report: ${response.status} ${response.statusText}`);
      }

      console.log('Question report submitted successfully:', { questionId, eval_id, reportText, type });
      
      // Show success message instead of closing immediately
      setIsSuccess(true);
    } catch (error) {
      console.error('Error submitting report:', error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium mb-4">Report Question</h3>
        
        {isSuccess ? (
          <div className="text-center py-8">
            <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              <svg className="w-6 h-6 mx-auto mb-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <p className="font-medium">Report submitted successfully!</p>
              <p className="text-sm mt-2">Reported questions will be excluded from the evaluation if the report is valid.</p>
            </div>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Close ({countdown})
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="reportText" className="block text-sm font-medium text-gray-700 mb-2">
              Reported questions will be excluded from the evaluation if the report is valid.
            </label>
            <textarea
              id="reportText"
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              rows="4"
              placeholder="Please describe the issue with this question..."
              required
              disabled={isSubmitting}
            />
            {question?.uuid && (
              <div className="mt-2 text-xs text-gray-400">
                Question ID: {question.uuid}
              </div>
            )}
          </div>
          
          {submitError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              <p className="text-sm">{submitError}</p>
            </div>
          )}
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className={`px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="inline-block animate-spin mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                'Report'
              )}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

export default ReportQuestionPopup;
